import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, MessageSquare, Zap, Megaphone } from 'lucide-react';
import { useApi } from '../../utils/useApi';

interface GymDetails {
    name: string;
    phoneNumbers: string[];
    notificationName?: string;
}

export interface UnifiedLog {
    id: string;
    source: 'automatic' | 'campaign';
    campaign: { id: string; name: string; templateName: string | null } | null;
    triggerEvent: string;
    messageType?: string | null;
    phoneNumber: string;
    messageText: string;
    status: string;
    errorMessage?: string | null;
    createdAt: string;
    sentAt?: string | null;
    costPerMessage?: number | string | null;
    totalCost?: number | string | null;
    member?: {
        fullName: string;
        displayName: string;
    } | null;
}

const STATUS_STYLES: Record<string, { badge: string; dot: string }> = {
    SENT: { badge: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
    DELIVERED: { badge: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
    SEEN: { badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
    FAILED: { badge: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
    PENDING: { badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
    OPTED_OUT: { badge: 'bg-gray-200 text-gray-600', dot: 'bg-gray-400' }
};

const statusStyle = (status: string) =>
    STATUS_STYLES[status?.toUpperCase()] || { badge: 'bg-gray-100 text-gray-700', dot: 'bg-gray-500' };

/**
 * Unified notification log: every WhatsApp message sent for this tenant,
 * whether by an automatic event trigger or a manual campaign. The Source
 * column tells them apart; campaign rows carry the campaign name.
 */
export default function UnifiedNotificationLogs({ gymId }: { gymId: string }) {
    const { fetchWithAuth } = useApi();
    const [page, setPage] = useState(1);
    const limit = 20;

    // Gym details for placeholder substitution in old logs' previews
    const { data: gymData } = useQuery({
        queryKey: ['gym-details', gymId],
        queryFn: async () => {
            const response = await fetchWithAuth(`/api/gyms/${gymId}`);
            return response.data as GymDetails;
        },
        enabled: !!gymId
    });

    const { data: logsData, isLoading, refetch, isRefetching } = useQuery({
        queryKey: ['unified-notification-logs', gymId, page],
        queryFn: async () => {
            return await fetchWithAuth(`/api/notifications/logs?gymId=${gymId}&page=${page}&limit=${limit}`);
        },
        enabled: !!gymId
    });

    const logs: UnifiedLog[] = logsData?.data || [];
    const totalPages = logsData?.pagination?.totalPages || 0;
    const totalCount = logsData?.pagination?.total || 0;

    const substituteParams = (text: string, member?: UnifiedLog['member']) => {
        if (!text) return '';
        let formatted = text;

        // Try to replace placeholders if they exist (for old logs or manual templates)
        if (formatted.includes('{{')) {
            const memberName = member?.displayName || member?.fullName || 'Rudy';
            const gymName = gymData?.notificationName || gymData?.name || 'FitLife Gym';

            // OTP templates use param 1 for the verification code, not the member name;
            // the actual code isn't stored, so show it masked
            const isOtp = formatted.includes('verification code');

            const replacements: Record<string, string> = {
                '1': isOtp ? '******' : memberName,
                '2': `Basic Plan at ${gymName}`,
                '3': '₹2000',
                '4': '24-Mar-2026',
                '5': '25 Dec 2025',
                '6': '₹12,000',
                '7': 'INV-2025-001'
            };

            Object.entries(replacements).forEach(([key, value]) => {
                formatted = formatted.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
            });
        }
        return formatted;
    };

    const formatWhatsAppText = (text: string, member?: UnifiedLog['member']) => {
        const formatted = substituteParams(text, member);

        // Bold: *text* -> <strong>text</strong>
        return formatted.split('\n').map((line, i) => {
            const formattedLine = line.replace(/\*(.*?)\*/g, '<strong class="text-gray-900 font-bold">$1</strong>');
            return <div key={i} dangerouslySetInnerHTML={{ __html: formattedLine }} />;
        });
    };

    const formatDateTime = (dateStr: string) => {
        const date = new Date(dateStr);
        return new Intl.DateTimeFormat('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        }).format(date);
    };

    if (isLoading) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                <RefreshCw className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-4" />
                <p className="text-gray-500">Loading notification history...</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => refetch()}
                        disabled={isRefetching}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title="Refresh Logs"
                    >
                        <RefreshCw className={`h-5 w-5 ${isRefetching ? 'animate-spin text-blue-600' : ''}`} />
                    </button>
                    <span className="text-sm font-medium text-gray-500">
                        Notification Logs ({totalCount})
                    </span>
                </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Date & Time</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Recipient</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Source</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Event</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Type</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Cost</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Message Content</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {logs.map((log) => (
                                <tr key={log.id} className="hover:bg-blue-50/30 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600 font-medium">
                                        {formatDateTime(log.createdAt)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className={`text-sm font-bold ${log.member ? 'text-gray-900' : 'text-amber-600'}`}>
                                            {log.member?.displayName || log.member?.fullName || (log.triggerEvent === 'WHATSAPP_VERIFICATION' ? 'Verification (Guest)' : 'Unknown Member')}
                                        </div>
                                        <div className="text-xs text-gray-500 font-mono">{log.phoneNumber}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {log.source === 'campaign' ? (
                                            <div>
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700">
                                                    <Megaphone className="h-3 w-3" />
                                                    Campaign
                                                </span>
                                                {log.campaign?.name && (
                                                    <div className="text-[11px] text-gray-500 mt-1 max-w-[140px] truncate" title={log.campaign.name}>
                                                        {log.campaign.name}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700">
                                                <Zap className="h-3 w-3" />
                                                Automatic
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="px-2 py-1 text-[10px] font-bold font-mono bg-gray-100 rounded border border-gray-200 text-gray-600">
                                            {log.source === 'campaign' ? (log.campaign?.templateName || 'CAMPAIGN') : log.triggerEvent}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {log.messageType ? (
                                            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700">
                                                {log.messageType}
                                            </span>
                                        ) : (
                                            <span className="text-gray-300 text-xs">—</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-col items-start gap-1">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusStyle(log.status).badge}`}>
                                                <div className={`w-1.5 h-1.5 rounded-full mr-2 ${statusStyle(log.status).dot}`} />
                                                {log.status?.replace(/_/g, ' ')}
                                            </span>
                                            {log.status?.toUpperCase() === 'FAILED' && log.errorMessage === 'Insufficient message credits' && (
                                                <span className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded border border-red-100 font-bold uppercase tracking-wider">
                                                    No credits
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <div className="text-sm font-bold text-gray-900">
                                            ₹{Number(log.totalCost || 0).toFixed(2)}
                                        </div>
                                        {log.costPerMessage && Number(log.costPerMessage) > 0 && (
                                            <div className="text-[10px] text-gray-500">
                                                ₹{Number(log.costPerMessage).toFixed(2)} /msg
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-xs text-gray-500 max-w-xs relative group cursor-help overflow-visible">
                                        <div className="truncate group-hover:invisible">
                                            {substituteParams(log.messageText, log.member)}
                                        </div>
                                        {/* Hover Detail Card */}
                                        <div className="hidden group-hover:block absolute left-0 top-0 z-10 w-80 p-4 bg-white border border-gray-200 rounded-xl shadow-2xl animate-in fade-in zoom-in-95 duration-200 pointer-events-none">
                                            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
                                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Message Preview</span>
                                                <div className="ml-auto text-[10px] font-bold text-gray-500">
                                                    Cost: ₹{Number(log.totalCost || 0).toFixed(2)}
                                                </div>
                                            </div>
                                            <div className="bg-[#DCF8C6] p-3 rounded-lg rounded-tl-none shadow-sm text-[12px] text-gray-800 leading-relaxed message-bubble">
                                                {formatWhatsAppText(log.messageText, log.member)}
                                            </div>
                                            {log.status?.toUpperCase() === 'FAILED' && (
                                                <div className="mt-3 p-2 bg-red-50 rounded border border-red-100 text-[10px] text-red-600 font-medium">
                                                    {log.errorMessage || 'Delivery failed. Please check WhatsApp settings.'}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {logs.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex flex-col items-center gap-2">
                                            <MessageSquare className="h-8 w-8 text-gray-300" />
                                            <p>No notifications recorded yet.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {totalCount > 0 && (
                <div className="flex items-center justify-between bg-white px-4 py-3 sm:px-6 rounded-xl border border-gray-200 shadow-sm">
                    <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(page * limit, totalCount)}</span> of{' '}
                        <span className="font-medium">{totalCount}</span> results
                    </p>
                    {totalPages > 1 && (
                        <div className="flex gap-1">
                            <button
                                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                disabled={page === 1}
                                className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                            >
                                Previous
                            </button>
                            <span className="px-3 py-1 text-sm text-gray-500">
                                Page {page} of {totalPages}
                            </span>
                            <button
                                onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={page === totalPages}
                                className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
